/**
 * Store Implementation Module
 * 
 * This module provides the implementation of reactive stores.
 * It includes both the legacy store implementation (oldAppStore) and
 * the new reactive store implementation.
 * 
 * The reactive store implementation uses the Proxy API to intercept
 * property changes and notify subscribers when properties change.
 * 
 * This module also registers the default store with the store registry
 * so that components can access it without creating circular dependencies.
 */

// import { atom, Atom,deepMap, WritableAtom } from "nanostores";
// import { ParamateConfigurator } from "../ext/paramate-frontend/index.ts";
import type { IReactiveComponent, IReactiveStore } from "../as-components";
import { registerStore } from "./store-registry.ts";

import { langAbbrev, LangKey } from "../data/langs.ts";
import stream from 'mithril/stream'
import { appSuiteRootPath, DEFAULT_ASSETS_PATH, DEFAULT_ICON_LIST, DEFAULT_SERVER_ROOT, fullAppSuiteRootURL } from "../data/constants.ts";

// /**
//  * Interface for the legacy app store
//  * 
//  * This interface defines the structure of the legacy app store.
//  * It is kept for backward compatibility with existing code.
//  */
// interface OldAppStore {
//   configurator?: any;
//   selected: number;
//   text: string;
//   $language: stream<LangKey>;
//   isViewportRegistered: boolean;
//   $languageData: stream<Record<string, Record<string, string>>>;
//   textSubscribers:Record<string,HTMLElement>;
// }

// /**
//  * Legacy app store implementation
//  * 
//  * This is the legacy store implementation that uses mithril streams.
//  * It is kept for backward compatibility with existing code.
//  */
// export const oldAppStore: OldAppStore = {
//   configurator: undefined,
//   selected: 1,
//   text: 'Hello',
//   $language: stream('en'),
//   isViewportRegistered: false,
//   $languageData: stream({}),
//   textSubscribers:{}
// };

// /**
//  * Set up language change listener for the legacy store
//  * 
//  * This code listens for language changes in the legacy store and
//  * notifies all text subscribers when the language changes.
//  */
// oldAppStore.$language.map(lang=>{
//   // console.log("Language changed to", lang);
//   Object.entries(oldAppStore.textSubscribers).forEach(([key,subscriber])=>{
//     // console.log("Dispatching lang-changed event to", subscriber);
//     subscriber.dispatchEvent(new CustomEvent('lang-changed', {
//       detail: {
//         lang: lang,
//         newText: getText(key)
//       }
//     }));
//   });
// });
  
// /**
//  * Subscribe to text changes in the legacy store
//  * 
//  * @param key The text key to subscribe to
//  * @param subscriber The element to notify when the text changes
//  */
// export function subscribeToText(key: string, subscriber: HTMLElement) {
//   oldAppStore.textSubscribers[key] = subscriber;
//   console.log("Subscribed to text", key, "with subscriber", subscriber);
// }

// /**
//  * Get text for a key in the current language
//  * 
//  * @param key The text key to get
//  * @returns The text for the key in the current language, or a placeholder if not found
//  */
// export const getText = (key: string) => {
//   console.log("Getting text for", key, "in", oldAppStore.$language());
//   return oldAppStore.$languageData()[oldAppStore.$language()]?.[key]??"<<NO TEXT FOUND>>";
// };


/**
 * Notifies all subscribers of a property change
 * 
 * This function is called by the Proxy handler when a property changes.
 * It notifies all subscribers of the changed property.
 * 
 * @param store The store object
 * @param prop The property that changed
 */
function notifySubscribers(store: IReactiveStore, prop: string) {
  const components = store.subscribers.get(prop);
  
  if (components) {
    components.forEach(component => {
      component.updateSubbedValues({[prop]: store[prop]});
    });
  }
}

function triggerSideEffects(store: IReactiveStore, prop: string) {
  const effects = store.sideEffects.get(prop);
  if (effects) {
    effects.forEach(effect => effect(store[prop]));
  }
}

/**
 * Creates a reactive store with the provided schema
 * 
 * This function creates a reactive store that notifies subscribers
 * when properties change. It uses the Proxy API to intercept property
 * changes and notify subscribers.
 * 
 * @param schema Initial store schema with default values
 * @returns A proxy object that notifies subscribers when properties change
 */
export function reactiveStore<T extends Record<string, any>>(schema: T): IReactiveStore {
  // Create a store object with the schema and subscribers map
  const storeObj = {
    ...schema,
    emitUpdateEvent:true,
    sideEffects:new Map<string, Set<(value: any) => void>>(),

    subscribers: new Map<string, Set<IReactiveComponent>>(),
    
    /**
     * Subscribe a component to specific properties
     * 
     * This method adds a component to the list of subscribers for the
     * specified properties. When those properties change, the component
     * will be notified.
     * 
     * @param component The component to subscribe
     * @param props Array of property names to subscribe to
     * @returns Object with current values of subscribed properties
     */
    subscribe(component: IReactiveComponent, props: string[]): {[key: string]: any} {
      // devlog(this, "subscribing to", props);
      props.forEach(prop => {
        if (!this.subscribers.has(prop)) {
          this.subscribers.set(prop, new Set<IReactiveComponent>());
        }
        this.subscribers.get(prop)?.add(component);
      });
      return props.reduce((acc, prop) => {
        acc[prop] = this[prop as keyof typeof this];
        return acc;
      }, {});
    },
    
    /**
     * Add a side effect that triggers when a property changes
     * 
     * @param prop The property to watch for changes
     * @param effect The function to call when the property changes
     * @returns A function that when called will remove this side effect
     */
    addSideEffect(prop: string, effect: (value: any) => void): () => void {
      if (!this.sideEffects.has(prop)) {
        this.sideEffects.set(prop, new Set<(value: any) => void>());
      }
      this.sideEffects.get(prop)?.add(effect);
      
      // Return a function to remove this specific effect
      return () => this.removeSideEffect(prop, effect);
    },
    
    /**
     * Remove a specific side effect for a property
     * 
     * @param prop The property the effect is registered for
     * @param effect The effect function to remove
     * @returns Boolean indicating if the effect was successfully removed
     */
    removeSideEffect(prop: string, effect: (value: any) => void): boolean {
      const effects = this.sideEffects.get(prop);
      if (!effects) return false;
      
      const result = effects.delete(effect);
      
      if (effects.size === 0) {
        this.sideEffects.delete(prop);
      }
      
      return result;
    },
    
    /**
     * Remove all side effects for a specific property
     * 
     * @param prop The property to remove all side effects for
     * @returns Boolean indicating if any effects were removed
     */
    clearSideEffects(prop: string): boolean {
      return this.sideEffects.delete(prop);
    },

    /**
     * Unsubscribe a component from all properties
     * 
     * This method removes a component from the list of subscribers
     * for all properties.
     * 
     * @param component The component to unsubscribe
     */
    unsubscribe(component: IReactiveComponent) {
      this.subscribers.forEach((components, prop) => {
        components.delete(component);
        if (components.size === 0) {
          this.subscribers.delete(prop);
        }
      });
    },
  };
  
  // Create a proxy to intercept property changes
  return new Proxy(storeObj, {
    set(target, prop, value) {
      target[prop as keyof typeof target] = value;
      notifySubscribers(target, prop as string);
      triggerSideEffects(target, prop as string);
      if (target.emitUpdateEvent) {
        window.dispatchEvent(new CustomEvent('store-update', {
          detail: {
            store: target,
            prop: prop as string,
            value: value
          }
        }));
      }
      return true;
    }
  });
}
export interface IconListing {
  [iconName:string]:{[iconVariant:string]:string}
}
/**
 * Default store instance
 * 
 * This is the default store instance that is used by components
 * that don't specify a store name.
 */
export const commonAppData = reactiveStore({
  userData: null,
  isAuthenticated: false,
  pendingAuth: true,
  testText: "test",
  serverRoot: DEFAULT_SERVER_ROOT,
  assetsPath: DEFAULT_ASSETS_PATH,
  appId:undefined,
  appLangFileName:undefined,

  get imgDir() {
    if (window?.USE_LOCAL_ICONS) return `${fullAppSuiteRootURL}/${this.assetsPath}/img`;
    else return `https://apps.trinckle.com/${this.assetsPath}/img`
  },

  _iconList:  DEFAULT_ICON_LIST
});

// Register the store with the registry
registerStore('default', commonAppData);/**
/**
 * Proxy that automatically prefixes icon paths with HEADER_IMG_DIR
 */

export const IconList = new Proxy(commonAppData._iconList, {
  get(target, prop) {
    const value = target[prop as keyof typeof target];
    if (typeof value === 'object') {
      return new Proxy(value, {
        get(target, prop) {
          const path = target[prop as keyof typeof target];
          return `${commonAppData.imgDir}/${path}`;
        }
      });
    }
    return value;
  }
});
 